import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, SxProps, Theme, Stack, Typography } from '@mui/material';

const common_sidebar_items: Record<string, SidebarItem> = {
  role: {
    title: 'Your role',
    description: 'Are you a student or an adult?',
    iconName: 'User01',
  },
  create_account: {
    title: 'Create Account',
    description: 'Name, email, and password',
    iconName: 'LockUnlocked01',
  },
  aspirations: {
    title: 'Your aspirations',
    description: 'How can Backrs help you?',
    iconName: 'Target04',
  },
};

type SidebarItem = Readonly<{
  title: string;
  description: string;
  iconName: AppIcons;
}>;

type OnboardingFlowsAndStart = 'student' | 'adult' | 'educator' | 'employer' | 'START';

const RoleOnboardingSidebarItemsMap: Record<OnboardingFlowsAndStart, SidebarItem[]> = {
  student: [
    common_sidebar_items.role,
    common_sidebar_items.create_account,
    common_sidebar_items.aspirations,
    {
      title: 'Recommendations',
      description: 'Get personalized suggestions',
      iconName: 'UploadCloud02',
    },
  ],
  adult: [
    common_sidebar_items.role,
    common_sidebar_items.create_account,
    {
      title: 'Engage in Networks',
      description: 'Get personalized suggestions',
      iconName: 'Dataflow01',
    },
    {
      title: 'Recommendations',
      description: 'Get personalized suggestions',
      iconName: 'UploadCloud02',
    },
  ],
  educator: [
    common_sidebar_items.role,
    {
      title: 'Educator Needs',
      description: 'How can Backrs help you?',
      iconName: 'Telescope',
    },
    {
      ...common_sidebar_items.create_account,
      iconName: 'CheckSquareBroken',
    },
    {
      title: 'Join Networks',
      description: 'Get personalized suggestions',
      iconName: 'Dataflow01',
    },
  ],
  employer: [
    common_sidebar_items.role,
    {
      title: 'Objective',
      description: 'How can Backrs help you?',
      iconName: 'Trophy01',
    },
    {
      ...common_sidebar_items.create_account,
      iconName: 'CheckSquareBroken',
    },
    {
      title: 'Recruitment',
      description: 'Get personalized suggestions',
      iconName: 'Dataflow03',
    },
  ],
  START: [common_sidebar_items.role],
};

// For educator, we have an additional question so we cannot just display the sidebar items via index so we need to map the question index to the correct sidebar item index
// 0: WHAT_DO_YOU_DO
// 1: YOUR_NEEDS
// 2: STUDENT_NEEDS
// 3: CREATE_ACCOUNT
// 4: ENGAGE_IN_NETWORKS
const EDUCATOR_QUESTION_TO_SIDEBAR_MAP: Record<number, number> = {
  0: 0,
  1: 1,
  2: 1,
  3: 2,
  4: 3,
};

type OnboardingSidebarItemProps = Readonly<{
  lastItemIndex: number;
  index: number;
  item: SidebarItem;
  selectedIndex: number;
}>;

const OnboardingSidebarItem = ({
  lastItemIndex,
  index,
  item,
  selectedIndex,
}: OnboardingSidebarItemProps) => {
  return (
    <Box key={item.title} sx={(theme) => sidebarItemContainerSx(theme, selectedIndex === index)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={iconContainerSx}>
          <AppIcon diameter="24px" iconName={item.iconName} color={'Colors/Text/text-primary'} />
        </Box>
        {/* divider */}
        {lastItemIndex !== index && <Box sx={dividerSx}></Box>}
      </Box>
      <Stack>
        <Typography color={(theme) => theme.backrs.colors['Gray/700']} variant="text-md-semibold">
          {item.title}
        </Typography>
        <Typography color={(theme) => theme.backrs.colors['Gray/600']} variant="text-md-regular">
          {item.description}
        </Typography>
      </Stack>
    </Box>
  );
};

type OnboardingSidebarItemsProps = Readonly<{
  flow: OnboardingFlowsAndStart;
  selectedIndex: number;
}>;

export function OnboardingSidebarItems({ flow, selectedIndex }: OnboardingSidebarItemsProps) {
  const getSidebarItems = (onboardingFlow: OnboardingFlowsAndStart) => {
    if (onboardingFlow === 'START') {
      return [common_sidebar_items.role];
    }

    return RoleOnboardingSidebarItemsMap[onboardingFlow];
  };

  const sidebarItems = getSidebarItems(flow);

  // map the question index to the correct sidebar item index (needed for just educator flow)
  const getMappedSelectedIndex = (flow: OnboardingFlowsAndStart, questionIndex: number): number => {
    if (flow === 'educator' && questionIndex in EDUCATOR_QUESTION_TO_SIDEBAR_MAP) {
      return EDUCATOR_QUESTION_TO_SIDEBAR_MAP[questionIndex];
    }

    return questionIndex;
  };

  const mappedSelectedIndex = getMappedSelectedIndex(flow, selectedIndex);

  return (
    <Box>
      {sidebarItems?.length > 0 &&
        sidebarItems.map((item, index) => (
          <OnboardingSidebarItem
            key={item.title}
            item={item}
            lastItemIndex={sidebarItems.length - 1}
            index={index}
            selectedIndex={mappedSelectedIndex}
          />
        ))}
    </Box>
  );
}

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '2px',
  height: '26px',
  borderRadius: backrs.radius['radius-xxs'],
  backgroundColor: backrs.colors['Gray/200'],
  marginY: backrs.spacing['spacing-xs'],
});

const sidebarItemContainerSx = (theme: Theme, isSelected: boolean) => ({
  display: 'flex',
  gap: theme.backrs.spacing['spacing-xl'],
  opacity: isSelected ? 1 : '0.6',
  transition: 'opacity 300ms ease-in-out',
  userSelect: 'none',
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Gray/400']}`,
  padding: '12px',
  backgroundColor: backrs.colors['Colors/Base/White'],
  justifyContent: 'center',
  alignItems: 'center',
});
